import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { STEPS, OFFERINGS, IMAGES, PATHWAY } from "@/data";

const PageHeader = ({ overline, title, italic, subtitle }) => (
  <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-40 md:pt-52 pb-20">
    <Reveal>
      <div className="overline text-gold mb-8 flex items-center gap-3">
        <span className="h-px w-10 bg-gold" /> {overline}
      </div>
      <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] max-w-5xl">
        {title} <span className="italic text-gold">{italic}</span>
      </h1>
    </Reveal>
    {subtitle && (
      <Reveal delay={0.15}>
        <p className="text-white/60 text-lg md:text-xl leading-relaxed mt-10 max-w-2xl">{subtitle}</p>
      </Reveal>
    )}
  </section>
);

const Chapter = ({ step, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.2, 1, 1, 0.2]);
  return (
    <div ref={ref} className="grid md:grid-cols-12 gap-8 md:gap-14 py-14 md:py-20 border-b border-white/10">
      <div className="md:col-span-4">
        <div className="md:sticky md:top-32">
          <motion.span style={{ opacity }} className="font-serif text-7xl md:text-8xl lg:text-9xl text-gold leading-none block">
            {step.n}
          </motion.span>
        </div>
      </div>
      <div className="md:col-span-7 md:col-start-6 self-center">
        <Reveal>
          <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-5">{step.title}</h3>
          <p className="text-white/60 text-lg leading-relaxed max-w-xl">{step.body}</p>
          {step.objective && (
            <div className="mt-6 flex items-start gap-3 max-w-xl border-l-2 border-gold/50 pl-4">
              <span className="overline text-gold shrink-0 mt-1">Objective</span>
              <span className="text-white/75 leading-relaxed">{step.objective}</span>
            </div>
          )}
        </Reveal>
      </div>
    </div>
  );
};

const CAMPAIGN_TARGETS = [
  ["Property Management", 100],
  ["Medical / Dental", 100],
  ["Professional Offices", 125],
  ["Retail Commercial Properties", 75],
  ["Industrial / Warehouse", 50],
  ["Schools / Private Facilities", 50],
];

const FUNNEL = [
  { n: "500", label: "Target Accounts", w: 100 },
  { n: "350", label: "Decision Makers Identified", w: 84 },
  { n: "200", label: "Meaningful Contacts & Conversations", w: 66 },
  { n: "40", label: "Qualified Opportunities", w: 48 },
  { n: "15", label: "Walkthrough / Bid Opportunities", w: 32 },
  { n: "5", label: "New Commercial Accounts", w: 22 },
];

const CAMPAIGN_REVENUE = [
  ["Medical Office", "$1,500", "$18,000"],
  ["Professional Office", "$2,000", "$24,000"],
  ["Retail Property", "$2,500", "$30,000"],
  ["Apartment Community", "$4,000", "$48,000"],
  ["Office Complex", "$5,000", "$60,000"],
];

const CampaignExample = () => (
  <section data-testid="campaign-example" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 md:py-40">
    <Reveal>
      <div className="overline text-gold mb-6 flex items-center gap-3">
        <span className="h-px w-10 bg-gold" /> Example 90-Day Campaign
      </div>
      <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none max-w-3xl">
        How it works for <span className="italic text-gold">"ABC Cleaning."</span>
      </h2>
    </Reveal>

    <div className="grid md:grid-cols-12 gap-10 mt-16">
      <Reveal className="md:col-span-6">
        <div className="border-l-2 border-gold/50 pl-6">
          <div className="overline text-white/40 mb-4">The Problem</div>
          <p className="text-white/70 text-lg leading-relaxed">
            ABC provides excellent cleaning services — but the owner is responsible for operations, employees, estimates, customer service and sales. Commercial prospecting only happens when business slows down, so there's no predictable pipeline.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.15} className="md:col-span-6 self-center">
        <p className="font-serif text-2xl md:text-3xl tracking-tight text-white/90 leading-snug">
          Our 10-Step Commercial Contract Acquisition Process <span className="italic text-gold">changes that.</span>
        </p>
      </Reveal>
    </div>

    <Reveal>
      <div className="mt-20">
        <div className="overline text-gold mb-6">Step One — 500 Targeted Accounts</div>
        <p className="text-white/60 text-lg leading-relaxed max-w-2xl mb-8">
          We identify 500 targeted commercial accounts within ABC's service area. Rather than randomly calling businesses, we divide them into high-value categories:
        </p>
        <div className="border border-white/10">
          <div className="grid grid-cols-2 bg-[#14141a] px-6 py-4">
            <span className="overline text-white/40">Target Market</span>
            <span className="overline text-white/40 text-right">Accounts Researched</span>
          </div>
          {CAMPAIGN_TARGETS.map(([market, count]) => (
            <div key={market} className="grid grid-cols-2 px-6 py-4 border-t border-white/10 items-center">
              <span className="text-white/80">{market}</span>
              <span className="text-white/80 text-right font-serif text-xl">{count}</span>
            </div>
          ))}
          <div className="grid grid-cols-2 px-6 py-5 border-t border-gold/40 bg-gold/5 items-center">
            <span className="text-gold overline">Total</span>
            <span className="text-gold text-right font-serif text-2xl">500</span>
          </div>
        </div>
      </div>
    </Reveal>

    <Reveal>
      <div className="mt-20">
        <div className="overline text-gold mb-8">The System Funnel</div>
        <div className="space-y-3">
          {FUNNEL.map((f) => (
            <div
              key={f.label}
              className="mx-auto flex items-center gap-5 bg-[#0F0F13] border border-white/10 border-l-2 border-l-gold px-5 md:px-6 py-5"
              style={{ width: `${f.w}%`, minWidth: "270px" }}
            >
              <span className="font-serif text-3xl md:text-4xl text-gold leading-none w-16 md:w-20 shrink-0">{f.n}</span>
              <span className="text-white/75 text-sm md:text-base">{f.label}</span>
            </div>
          ))}
        </div>
      </div>
    </Reveal>

    <Reveal>
      <div className="mt-20">
        <div className="overline text-gold mb-6">Example Revenue — 5 New Accounts</div>
        <div className="border border-white/10">
          <div className="grid grid-cols-3 bg-[#14141a] px-5 md:px-6 py-4">
            <span className="overline text-white/40">Account</span>
            <span className="overline text-white/40 text-right">Monthly</span>
            <span className="overline text-white/40 text-right">Annually</span>
          </div>
          {CAMPAIGN_REVENUE.map(([account, monthly, annual]) => (
            <div key={account} className="grid grid-cols-3 px-5 md:px-6 py-4 border-t border-white/10 items-center">
              <span className="text-white/80 text-sm md:text-base">{account}</span>
              <span className="text-white/80 text-right text-sm md:text-base">{monthly}</span>
              <span className="text-white/80 text-right text-sm md:text-base">{annual}</span>
            </div>
          ))}
          <div className="grid grid-cols-3 px-5 md:px-6 py-5 border-t border-gold/40 bg-gold/5 items-center">
            <span className="text-gold overline">Total</span>
            <span className="text-gold text-right font-serif text-lg md:text-2xl">$15,000</span>
            <span className="text-gold text-right font-serif text-lg md:text-2xl">$180,000</span>
          </div>
        </div>
      </div>
    </Reveal>

    <Reveal>
      <div className="mt-16 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
        <div className="bg-[#0F0F13] p-10 text-center">
          <div className="font-serif text-5xl md:text-6xl text-gold">$15,000</div>
          <div className="overline text-white/50 mt-3">potential new recurring monthly revenue</div>
        </div>
        <div className="bg-[#0F0F13] p-10 text-center">
          <div className="font-serif text-5xl md:text-6xl text-gold">$180,000</div>
          <div className="overline text-white/50 mt-3">potential annual contract value</div>
        </div>
      </div>
      <p className="text-white/60 text-center mt-8 text-lg max-w-2xl mx-auto">
        And with our systems and partnership, cleaning contracts can <span className="text-white">compound quickly</span>.
      </p>
      <p className="text-white/30 text-xs text-center mt-4">Illustrative example. Actual results vary by industry, market, and response rates.</p>
    </Reveal>
  </section>
);

const OfferingBlock = () => (
  <section data-testid="services-offerings" className="bg-[#0F0F13] border-y border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 md:py-36">
      <Reveal>
        <div className="overline text-gold mb-6">Full offering</div>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none mb-16 max-w-3xl">
          More than outreach — a complete <span className="italic text-gold">growth system</span>.
        </h2>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {OFFERINGS.map((o, i) => (
          <Reveal key={o.key} delay={i * 0.12} className="h-full">
            <div className="group h-full bg-[#0F0F13] p-10 hover:bg-[#14141a] transition-colors duration-500 flex flex-col">
              <span className="text-gold font-serif text-3xl mb-8 block">0{i + 1}</span>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-4 group-hover:text-gold transition-colors">{o.title}</h3>
              <p className="text-white/60 leading-relaxed">{o.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader
        overline="Services"
        title="How we win"
        italic="commercial work."
        subtitle="A deliberate, ten-step engine that turns cold commercial markets into a pipeline of qualified bid opportunities and loyal, long-term clients."
      />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-10">
        <Reveal className="relative overflow-hidden aspect-[21/9] border border-white/10">
          <img src={IMAGES.blueprint} alt="Architect blueprint" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050507] via-[#050507]/30 to-transparent" />
        </Reveal>
      </section>

      <section data-testid="manifesto" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        {STEPS.map((s, i) => (
          <Chapter key={s.n} step={s} index={i} />
        ))}
      </section>

      <section data-testid="pathway" className="bg-[#0F0F13] border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-32 text-center">
          <Reveal>
            <div className="overline text-gold mb-10">The Pathway</div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3 mb-16">
              {PATHWAY.map((p, i) => (
                <span key={p} className="flex items-center gap-4">
                  <span className="font-serif text-2xl md:text-4xl tracking-tight text-white/85">{p}</span>
                  {i < PATHWAY.length - 1 && <span className="text-gold text-xl">—</span>}
                </span>
              ))}
            </div>
            <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl tracking-tight leading-tight max-w-4xl mx-auto">
              We don't sell leads.<br />
              <span className="italic text-gold">We develop commercial accounts.</span>
            </h2>
          </Reveal>
        </div>
      </section>

      <CampaignExample />

      <OfferingBlock />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 text-center">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none mb-10">
            See where you fit <span className="italic text-gold">in the pipeline</span>.
          </h2>
          <Link
            to="/contact"
            data-testid="services-cta"
            className="group inline-flex items-center gap-3 bg-gold text-black overline px-10 py-5 hover:bg-[#F3E5AB] transition-colors"
          >
            Book a Free Consultation
            <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
